import React, { useState } from 'react';
import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import Modal from '../ui/Modal';
import AiButton from './AiButton';
import { useToast } from '../../contexts/ToastContext';
import { getErrorMessage } from '../../utils/errorMessage';
import { refreshAiStatus } from '../../hooks/useAiStatus';

interface AiTextDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  /** Rövid magyarázat a beállítások fölött. */
  intro?: React.ReactNode;
  /** A beállítások (stílus, megjegyzés stb.) - a hívó kezeli az állapotukat. */
  children?: React.ReactNode;
  generate: () => Promise<string>;
  generateLabel?: string;
  /** További művelet a kész szöveggel (pl. „Beillesztés”). Ha nincs, csak másolni lehet. */
  onUse?: (text: string) => void;
  useLabel?: string;
}

/**
 * Közös ablak a szöveget író AI funkciókhoz (mérkőzés-beszámoló, játékos-értékelés):
 * beállítások, generálás, a szöveg kézi javítása, másolás vagy beillesztés.
 */
const AiTextDialog: React.FC<AiTextDialogProps> = ({
  isOpen,
  onClose,
  title,
  intro,
  children,
  generate,
  generateLabel = 'Megírás AI-val',
  onUse,
  useLabel = 'Beillesztés',
}) => {
  const { showToast } = useToast();
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    setLoading(true);
    setError(null);
    try {
      setText(await generate());
    } catch (err) {
      setError(getErrorMessage(err, 'Az AI most nem tudott válaszolni.'));
    } finally {
      setLoading(false);
      refreshAiStatus();
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      showToast('A szöveg a vágólapra került.', 'success');
    } catch {
      showToast('A másolás nem sikerült - jelöld ki és másold kézzel.', 'error');
    }
  };

  const close = () => {
    setText('');
    setError(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={close} title={title}>
      <div className="space-y-4 pb-4">
        {intro && <div className="text-sm text-gray-500">{intro}</div>}
        {children}

        <div className="flex justify-end">
          <AiButton variant="solid" onClick={run} loading={loading} loadingText="Írom…">
            {text ? 'Újraírás' : generateLabel}
          </AiButton>
        </div>

        {error && (
          <p className="rounded-md bg-red-50 p-3 text-sm text-red-700" role="alert">
            {error}
          </p>
        )}

        {text && (
          <div className="space-y-2">
            <textarea
              rows={12}
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="Az AI által írt szöveg"
            />
            <p className="text-xs text-gray-500">
              Az AI hibázhat: olvasd át, és javíts bele, mielőtt továbbküldöd.
            </p>
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={copy}
                className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <ClipboardDocumentIcon className="h-4 w-4" />
                Másolás
              </button>
              {onUse && (
                <button
                  type="button"
                  onClick={() => {
                    onUse(text);
                    close();
                  }}
                  className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
                >
                  {useLabel}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AiTextDialog;
